import { Typography } from '@mui/material';
import { Palette } from '@mui/material/styles';
import styled, { css } from 'styled-components';
import { hexWithOpacity } from 'shared-ui';

import { DodStateTone } from './StateChip.types';

/** Text colour per tone. Neutral reads in the app's dark grey: its chart colour is a tint, not ink. */
const toneColor = (palette: Palette, tone: DodStateTone): string => {
  if (tone === 'ok') {
    return palette.chart.ok;
  }

  if (tone === 'warn') {
    return palette.chart.warn;
  }

  if (tone === 'bad') {
    return palette.chart.bad;
  }

  if (tone === 'info') {
    return palette.primary.main;
  }

  return palette.grey[900];
};

const toneTint = (palette: Palette, tone: DodStateTone): string =>
  hexWithOpacity(tone === 'neutral' ? palette.chart.neutral : toneColor(palette, tone), 0.2);

/**
 * The app's status badge (`ui/serverStatus`): 24 px tall, radius from the theme, a tinted
 * background and no border. Width follows the text instead of the badge's fixed 115 px, because a
 * chip may carry a prefix and a caption.
 */
export const Badge = styled.span<{ $tone: DodStateTone; $isClickable: boolean }>(
  ({ theme: { palette, shape }, $tone, $isClickable }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 8px;
    max-width: 100%;
    border-radius: ${shape.borderRadius}px;
    background-color: ${toneTint(palette, $tone)};
    cursor: ${$isClickable ? 'pointer' : 'default'};
  `,
);

/** `subtitle2` is the theme's 12 px / 700 face; the badge only adds its colour and the upper case. */
export const Text = styled(Typography)<{ $tone: DodStateTone }>(
  ({ theme: { palette }, $tone }) => css`
    color: ${toneColor(palette, $tone)};
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
);
